import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FiBell, FiPlus, FiX, FiTrash2, FiSearch, FiUsers, FiUserCheck,
    FiCheckCircle, FiRefreshCw, FiChevronLeft, FiChevronRight, FiChevronDown,
    FiUser, FiCalendar, FiInbox, FiSettings, FiTag, FiFolder, FiInfo, FiLink,
    FiAlertTriangle, FiArrowUp, FiArrowDown, FiSend, FiUserPlus, FiExternalLink,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import i18n from '../../../i18n';
import { cabecerasAuth, claves } from '../../../queries/cliente';

// Icono estable por clave; el label se resuelve con t() en el idioma activo
const ICONO_TIPO = { sistema: FiSettings, ticket: FiTag, proyecto: FiFolder, usuario: FiUser };
const CLAVES_TIPO = ['sistema', 'ticket', 'proyecto', 'usuario'];

const ICONO_ICONOS = { campana: FiBell, ticket: FiTag, info: FiInfo, alerta: FiAlertTriangle, usuario: FiUser };
const CLAVES_ICONO = ['campana', 'ticket', 'info', 'alerta', 'usuario'];

const ICONO_DESTINATARIO = { todos: FiUsers, admins: FiUserCheck, tecnicos: FiUserCheck, admins_tecnicos: FiUserCheck, especifico: FiUserPlus };
const CLAVES_DESTINATARIO = ['todos', 'admins', 'tecnicos', 'admins_tecnicos', 'especifico'];

const formatearFecha = (fecha) => new Intl.DateTimeFormat(i18n.language, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
}).format(new Date(fecha));

const formatearFechaRelativa = (fecha) => {
    const diffMs = Date.now() - new Date(fecha).getTime();
    const minutos = Math.floor(diffMs / 60000);
    if (minutos < 1) return i18n.t('gestorNotificaciones.ahora');
    if (minutos < 60) return i18n.t('gestorNotificaciones.haceMin', { n: minutos });
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return i18n.t('gestorNotificaciones.haceHoras', { n: horas });
    const dias = Math.floor(horas / 24);
    if (dias < 30) return i18n.t('gestorNotificaciones.haceDias', { n: dias });
    return new Date(fecha).toLocaleDateString(i18n.language);
};

const TipoIcono = ({ tipo }) => {
    const Icono = ICONO_TIPO[tipo] || ICONO_TIPO.sistema;
    return <Icono />;
};

const NotificacionesManager = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const TIPO_META = Object.fromEntries(CLAVES_TIPO.map((v) => [v, { label: t(`gestorNotificaciones.tipos.${v}`), icon: ICONO_TIPO[v] }]));
    const ICONO_OPCIONES = CLAVES_ICONO.map((value) => ({ value, label: t(`gestorNotificaciones.iconos.${value}`), icon: ICONO_ICONOS[value] }));
    const DESTINATARIO_OPCIONES = CLAVES_DESTINATARIO.map((value) => ({ value, label: t(`gestorNotificaciones.destinatarios.${value}`), icon: ICONO_DESTINATARIO[value] }));
    // Formulario de creación
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [destinatario, setDestinatario] = useState('todos');
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
    const [busquedaUsuario, setBusquedaUsuario] = useState('');
    const [mostrarListaUsuarios, setMostrarListaUsuarios] = useState(false);
    const [tipo, setTipo] = useState('sistema');
    const [titulo, setTitulo] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [link, setLink] = useState('');
    const [icono, setIcono] = useState('campana');
    const [prioridad, setPrioridad] = useState('normal');
    const [enviando, setEnviando] = useState(false);

    // Gestión
    const [seleccionadas, setSeleccionadas] = useState([]);
    const [eliminandoSeleccion, setEliminandoSeleccion] = useState(false);
    const [expandidas, setExpandidas] = useState([]);

    // Filtros
    const [filtroTipo, setFiltroTipo] = useState('todas');
    const [filtroEstado, setFiltroEstado] = useState('todas');
    const [busqueda, setBusqueda] = useState('');
    const [orden, setOrden] = useState('desc');

    // Paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const elementosPorPagina = 15;

    const token = localStorage.getItem('AuthToken');
    const tokenInfo = token ? jwtDecode(token) : {};
    const navigate = useNavigate();

    // Solo protege la ruta: los datos los traen las consultas de abajo
    useEffect(() => {
        if (tokenInfo.role !== 'admin') navigate('/');
        // eslint-disable-next-line
    }, []);

    const esAdmin = tokenInfo.role === 'admin';

    const [consultaNotificaciones, consultaUsuarios, consultaEstadisticas] = useQueries({
        queries: [
            {
                queryKey: claves.notificacionesAdmin(),
                queryFn: async () => {
                    const resp = await axios.get(`${BASE_URL}/notificaciones-admin/todas?limite=200`, cabecerasAuth());
                    return resp.data.success ? (resp.data.notificaciones || []) : [];
                },
                enabled: esAdmin,
            },
            {
                queryKey: claves.usuariosNotificables(),
                queryFn: async () => {
                    const resp = await axios.get(`${BASE_URL}/notificaciones-admin/usuarios`, cabecerasAuth());
                    return resp.data.success ? (resp.data.usuarios || []) : [];
                },
                enabled: esAdmin,
                staleTime: 5 * 60_000,
            },
            {
                queryKey: claves.estadisticasNotificaciones(),
                queryFn: async () => {
                    const resp = await axios.get(`${BASE_URL}/notificaciones-admin/estadisticas`, cabecerasAuth());
                    return resp.data.success ? resp.data.estadisticas : null;
                },
                enabled: esAdmin,
            },
        ],
    });

    useEffect(() => {
        if (consultaNotificaciones.isError || consultaUsuarios.isError || consultaEstadisticas.isError) {
            toast.error(t('gestorNotificaciones.errorCargarDatos'));
        }
    }, [consultaNotificaciones.isError, consultaUsuarios.isError, consultaEstadisticas.isError, t]);

    const notificaciones = useMemo(() => consultaNotificaciones.data || [], [consultaNotificaciones.data]);
    const usuarios = consultaUsuarios.data || [];
    const estadisticas = consultaEstadisticas.data || null;
    const cargando = consultaNotificaciones.isPending;
    const refrescando = consultaNotificaciones.isFetching && !consultaNotificaciones.isPending;

    /** Recarga la lista y sus estadísticas tras crear o borrar. */
    const cargarDatos = () => {
        setSeleccionadas([]);
        queryClient.invalidateQueries({ queryKey: claves.notificacionesAdmin() });
        queryClient.invalidateQueries({ queryKey: claves.estadisticasNotificaciones() });
    };

    const notificacionesFiltradas = useMemo(() => {
        let resultado = [...notificaciones];

        if (filtroTipo !== 'todas') {
            resultado = resultado.filter(n => n.tipo === filtroTipo);
        }

        if (filtroEstado === 'individuales') {
            resultado = resultado.filter(n => n.usuario_destinatario !== null);
        } else if (filtroEstado === 'masivas') {
            resultado = resultado.filter(n => n.usuario_destinatario === null);
        } else if (filtroEstado === 'leidas') {
            resultado = resultado.filter(n => n.usuario_destinatario && n.leida === true);
        } else if (filtroEstado === 'no_leidas') {
            resultado = resultado.filter(n => n.usuario_destinatario && n.leida === false);
        }

        if (busqueda.trim()) {
            const texto = busqueda.toLowerCase();
            resultado = resultado.filter(n =>
                n.titulo?.toLowerCase().includes(texto) ||
                n.mensaje?.toLowerCase().includes(texto) ||
                n.usuario_destinatario?.toLowerCase().includes(texto)
            );
        }

        resultado.sort((a, b) => {
            const diff = new Date(b.createdAt) - new Date(a.createdAt);
            return orden === 'desc' ? diff : -diff;
        });

        return resultado;
    }, [notificaciones, filtroTipo, filtroEstado, busqueda, orden]);

    useEffect(() => {
        setPaginaActual(1);
    }, [filtroTipo, filtroEstado, busqueda, orden]);

    const hayFiltrosActivos = filtroTipo !== 'todas' || filtroEstado !== 'todas' || busqueda.trim() !== '';

    const limpiarFiltros = () => {
        setFiltroTipo('todas');
        setFiltroEstado('todas');
        setBusqueda('');
    };

    const enviarNotificacion = async (e) => {
        e.preventDefault();

        if (!titulo.trim() || !mensaje.trim()) {
            toast.error(t('gestorNotificaciones.tituloMensajeObligatorios'));
            return;
        }

        if (destinatario === 'especifico' && !usuarioSeleccionado) {
            toast.error(t('gestorNotificaciones.seleccionaUsuarioDestinatario'));
            return;
        }

        setEnviando(true);
        try {
            const datos = {
                tipo,
                titulo: titulo.trim(),
                mensaje: mensaje.trim(),
                link: link.trim() || null,
                icono,
                prioridad,
                enviar_a_todos: destinatario === 'todos',
                enviar_a_admins: destinatario === 'admins' || destinatario === 'admins_tecnicos',
                enviar_a_tecnicos: destinatario === 'tecnicos' || destinatario === 'admins_tecnicos',
                usuario_destinatario: destinatario === 'especifico' ? usuarioSeleccionado : null,
            };

            const response = await axios.post(`${BASE_URL}/notificaciones`, datos, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success(response.data.message);
                limpiarFormulario();
                setMostrarFormulario(false);
                cargarDatos();
            }
        } catch (error) {
            console.error('Error al enviar notificación:', error);
            toast.error(t('gestorNotificaciones.errorEnviar'));
        } finally {
            setEnviando(false);
        }
    };

    const limpiarFormulario = () => {
        setTitulo('');
        setMensaje('');
        setLink('');
        setTipo('sistema');
        setIcono('campana');
        setPrioridad('normal');
        setUsuarioSeleccionado('');
        setBusquedaUsuario('');
        setDestinatario('todos');
    };

    const usuariosFiltrados = usuarios.filter(u => {
        const textoCompleto = `${u.nombre} ${u.apellidos} ${u.usuario} ${u.email}`.toLowerCase();
        return textoCompleto.includes(busquedaUsuario.toLowerCase());
    });

    const seleccionarUsuario = (usuario) => {
        setUsuarioSeleccionado(usuario.usuario);
        setBusquedaUsuario(`${usuario.nombre} ${usuario.apellidos} (@${usuario.usuario})`);
        setMostrarListaUsuarios(false);
    };

    const limpiarSeleccionUsuario = () => {
        setUsuarioSeleccionado('');
        setBusquedaUsuario('');
    };

    const eliminarNotificacion = async (id) => {
        if (!window.confirm(t('gestorNotificaciones.confirmarEliminarUna'))) return;

        try {
            const response = await axios.delete(`${BASE_URL}/notificaciones/${id}`, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success(t('gestorNotificaciones.notificacionEliminada'));
                cargarDatos();
            }
        } catch (error) {
            console.error('Error al eliminar:', error);
            toast.error(t('gestorNotificaciones.errorEliminar'));
        }
    };

    const eliminarSeleccionadas = async () => {
        if (seleccionadas.length === 0) return;
        if (!window.confirm(t('gestorNotificaciones.confirmarEliminarVarias', { count: seleccionadas.length }))) return;

        setEliminandoSeleccion(true);
        try {
            await Promise.all(seleccionadas.map(id => axios.delete(`${BASE_URL}/notificaciones/${id}`, {
                headers: { Authorization: token }
            })));
            toast.success(t('gestorNotificaciones.notificacionesEliminadas'));
            cargarDatos();
        } catch (error) {
            console.error('Error al eliminar seleccionadas:', error);
            toast.error(t('gestorNotificaciones.errorEliminarAlgunas'));
        } finally {
            setEliminandoSeleccion(false);
        }
    };

    const limpiarNotificacionesAntiguas = async () => {
        if (!window.confirm(t('gestorNotificaciones.confirmarLimpiarAntiguas'))) return;

        try {
            const response = await axios.delete(`${BASE_URL}/notificaciones-admin/limpiar-antiguas?dias=30`, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success(t('gestorNotificaciones.eliminadasCount', { count: response.data.eliminadas }));
                cargarDatos();
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(t('gestorNotificaciones.errorLimpiar'));
        }
    };

    const alternarSeleccion = (id) => {
        setSeleccionadas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const alternarExpandida = (id) => {
        setExpandidas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Paginación
    const totalPaginas = Math.ceil(notificacionesFiltradas.length / elementosPorPagina);
    const indiceUltimo = paginaActual * elementosPorPagina;
    const indicePrimero = indiceUltimo - elementosPorPagina;
    const notificacionesActuales = notificacionesFiltradas.slice(indicePrimero, indiceUltimo);

    const todasSeleccionadasEnPagina = notificacionesActuales.length > 0 &&
        notificacionesActuales.every(n => seleccionadas.includes(n.id));

    const alternarSeleccionPagina = () => {
        if (todasSeleccionadasEnPagina) {
            setSeleccionadas(prev => prev.filter(id => !notificacionesActuales.some(n => n.id === id)));
        } else {
            setSeleccionadas(prev => [...new Set([...prev, ...notificacionesActuales.map(n => n.id)])]);
        }
    };

    const cambiarPagina = (numeroPagina) => {
        if (numeroPagina >= 1 && numeroPagina <= totalPaginas) {
            setPaginaActual(numeroPagina);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const irAEnlace = (n) => {
        if (!n.link) return;
        if (n.link.startsWith('/')) navigate(n.link);
        else window.open(n.link, '_blank', 'noopener,noreferrer');
    };

    if (cargando) return <div className="loader-fullscreen"><Loader /></div>;

    return (
        <main className="site-width">
            <ToastContainer position="bottom-right" autoClose={3000} />

            <div className="gn-page">
                <div className="gn-header">
                    <div className="pagina-cabecera">
                        <h2>{t('gestorNotificaciones.titulo')}</h2>
                        <p>{t('gestorNotificaciones.subtitulo')}</p>
                    </div>
                    <div className="gn-header-acciones">
                        <button
                            className="gn-boton-icono"
                            onClick={cargarDatos}
                            disabled={refrescando}
                            title={t('gestorNotificaciones.actualizar')}
                            aria-label={t('gestorNotificaciones.actualizar')}
                        >
                            <FiRefreshCw className={refrescando ? 'gn-girando' : ''} />
                        </button>
                        <button
                            className="boton-primario gn-boton-nueva"
                            onClick={() => setMostrarFormulario(true)}
                        >
                            <FiPlus /> {t('gestorNotificaciones.nuevaNotificacion')}
                        </button>
                    </div>
                </div>

                {estadisticas && (
                    <div className="gn-stats">
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiBell /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.totalNotificaciones}</p>
                                <p className="gn-stat-label">{t('gestorNotificaciones.totalNotificaciones')}</p>
                                <p className="gn-stat-sub">{t('gestorNotificaciones.masivasIndividuales', { masivas: estadisticas.notificacionesMasivas, individuales: estadisticas.notificacionesIndividuales })}</p>
                            </div>
                        </div>
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiSend /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.individualesNoLeidas}</p>
                                <p className="gn-stat-label">{t('gestorNotificaciones.individualesNoLeidas')}</p>
                                <p className="gn-stat-sub">{t('gestorNotificaciones.pendientesRevision')}</p>
                            </div>
                        </div>
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiCheckCircle /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.individualesLeidas}</p>
                                <p className="gn-stat-label">{t('gestorNotificaciones.individualesLeidas')}</p>
                                <p className="gn-stat-sub">{t('gestorNotificaciones.porcentajeDelTotalIndividual', { porcentaje: estadisticas.porcentajeLeidasIndividuales })}</p>
                            </div>
                        </div>
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiUsers /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.totalUsuariosVerificados}</p>
                                <p className="gn-stat-label">{t('gestorNotificaciones.usuariosVerificados')}</p>
                                <p className="gn-stat-sub">{t('gestorNotificaciones.alcanceMasivas')}</p>
                            </div>
                        </div>
                    </div>
                )}

                {mostrarFormulario && (
                    <div className="gn-modal-overlay" onClick={() => setMostrarFormulario(false)}>
                        <div className="gn-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="gn-modal-header">
                                <h3><FiPlus /> {t('gestorNotificaciones.nuevaNotificacion')}</h3>
                                <button
                                    type="button"
                                    className="gn-boton-cerrar"
                                    onClick={() => setMostrarFormulario(false)}
                                    aria-label={t('gestorNotificaciones.cerrarFormulario')}
                                >
                                    <FiX />
                                </button>
                            </div>
                            <form onSubmit={enviarNotificacion} className="gn-modal-form">
                                <div className="campo-formulario">
                                    <label>{t('gestorNotificaciones.destinatario')}</label>
                                    <div className="gn-chips">
                                        {DESTINATARIO_OPCIONES.map(({ value, label, icon: Icono }) => (
                                            <button
                                                type="button"
                                                key={value}
                                                className={`gn-chip ${destinatario === value ? 'activo' : ''}`}
                                                onClick={() => {
                                                    setDestinatario(value);
                                                    if (value !== 'especifico') {
                                                        setBusquedaUsuario('');
                                                        setUsuarioSeleccionado('');
                                                    }
                                                }}
                                            >
                                                <Icono /> {label}
                                            </button>
                                        ))}
                                    </div>
                                    {destinatario === 'especifico' && (
                                        <div className="selector-usuario-busqueda">
                                            <div className="campo-busqueda-usuario">
                                                <FiSearch className="gn-icono-input" />
                                                <input
                                                    type="text"
                                                    value={busquedaUsuario}
                                                    onChange={(e) => {
                                                        setBusquedaUsuario(e.target.value);
                                                        setMostrarListaUsuarios(true);
                                                        if (!e.target.value) setUsuarioSeleccionado('');
                                                    }}
                                                    onFocus={() => setMostrarListaUsuarios(true)}
                                                    placeholder={t('gestorNotificaciones.buscarUsuarioPlaceholder')}
                                                    className="input-busqueda-usuario gn-input-con-icono"
                                                />
                                                {usuarioSeleccionado && (
                                                    <button
                                                        type="button"
                                                        className="boton-limpiar-usuario"
                                                        onClick={limpiarSeleccionUsuario}
                                                        title={t('gestorNotificaciones.limpiarSeleccion')}
                                                        aria-label={t('gestorNotificaciones.limpiarSeleccion')}
                                                    >
                                                        <FiX />
                                                    </button>
                                                )}
                                            </div>
                                            {mostrarListaUsuarios && busquedaUsuario && (
                                                <div className="lista-usuarios-desplegable">
                                                    {usuariosFiltrados.length > 0 ? (
                                                        usuariosFiltrados.map(u => (
                                                            <div
                                                                key={u.usuario}
                                                                className="item-usuario-desplegable"
                                                                onClick={() => seleccionarUsuario(u)}
                                                            >
                                                                <div className="info-usuario-item">
                                                                    <span className="nombre-usuario-item">
                                                                        {u.nombre} {u.apellidos}
                                                                    </span>
                                                                    <span className="usuario-item">@{u.usuario}</span>
                                                                </div>
                                                                <span className="email-usuario-item">{u.email}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="sin-resultados-usuario">
                                                            {t('gestorNotificaciones.sinUsuariosEncontrados')}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="grid-formulario">
                                    <div className="campo-formulario">
                                        <label>{t('gestorNotificaciones.tipoNotificacion')}</label>
                                        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="selector">
                                            {Object.entries(TIPO_META).map(([valor, meta]) => (
                                                <option key={valor} value={valor}>{meta.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="campo-formulario">
                                        <label>{t('gestorNotificaciones.prioridad')}</label>
                                        <select value={prioridad} onChange={(e) => setPrioridad(e.target.value)} className="selector">
                                            <option value="baja">{t('gestorNotificaciones.prioridades.baja')}</option>
                                            <option value="normal">{t('gestorNotificaciones.prioridades.normal')}</option>
                                            <option value="alta">{t('gestorNotificaciones.prioridades.alta')}</option>
                                            <option value="urgente">{t('gestorNotificaciones.prioridades.urgente')}</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="campo-formulario">
                                    <label>{t('gestorNotificaciones.icono')}</label>
                                    <div className="gn-chips">
                                        {ICONO_OPCIONES.map(({ value, label, icon: Icono }) => (
                                            <button
                                                type="button"
                                                key={value}
                                                className={`gn-chip ${icono === value ? 'activo' : ''}`}
                                                onClick={() => setIcono(value)}
                                            >
                                                <Icono /> {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="campo-formulario">
                                    <label>{t('gestorNotificaciones.tituloObligatorio')}</label>
                                    <input
                                        type="text"
                                        value={titulo}
                                        onChange={(e) => setTitulo(e.target.value)}
                                        placeholder={t('gestorNotificaciones.placeholderTitulo')}
                                        maxLength={255}
                                        required
                                    />
                                </div>

                                <div className="campo-formulario">
                                    <label>{t('gestorNotificaciones.mensajeObligatorio')}</label>
                                    <textarea
                                        value={mensaje}
                                        onChange={(e) => setMensaje(e.target.value)}
                                        placeholder={t('gestorNotificaciones.placeholderMensaje')}
                                        rows={4}
                                        required
                                    />
                                </div>

                                <div className="campo-formulario">
                                    <label>{t('gestorNotificaciones.enlaceOpcional')}</label>
                                    <div className="gn-campo-icono">
                                        <FiLink className="gn-icono-input" />
                                        <input
                                            type="text"
                                            value={link}
                                            onChange={(e) => setLink(e.target.value)}
                                            placeholder={t('gestorNotificaciones.placeholderEnlace')}
                                            className="gn-input-con-icono"
                                        />
                                    </div>
                                    <small>{t('gestorNotificaciones.hintEnlace')}</small>
                                </div>

                                <div className="botones-formulario">
                                    <button type="submit" className="boton-primario" disabled={enviando}>
                                        <FiSend /> {enviando ? t('gestorNotificaciones.enviando') : t('gestorNotificaciones.enviarNotificacion')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <div className="gn-toolbar">
                    {seleccionadas.length > 0 ? (
                        <div className="gn-toolbar-seleccion">
                            <span className="gn-toolbar-seleccion-info">
                                <FiCheckCircle /> {t('gestorNotificaciones.seleccionadas', { count: seleccionadas.length })}
                            </span>
                            <button className="gn-boton-cancelar" onClick={() => setSeleccionadas([])}>
                                {t('gestorNotificaciones.cancelar')}
                            </button>
                            <button
                                className="gn-boton-peligro"
                                onClick={eliminarSeleccionadas}
                                disabled={eliminandoSeleccion}
                            >
                                <FiTrash2 /> {eliminandoSeleccion ? t('gestorNotificaciones.eliminando') : t('gestorNotificaciones.eliminarSeleccionadas')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="gn-campo-icono gn-buscador">
                                <FiSearch className="gn-icono-input" />
                                <input
                                    type="text"
                                    placeholder={t('gestorNotificaciones.buscarPorTituloMensaje')}
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    className="campo-busqueda gn-input-con-icono"
                                />
                            </div>
                            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="selector">
                                <option value="todas">{t('gestorNotificaciones.todosLosTipos')}</option>
                                {Object.entries(TIPO_META).map(([valor, meta]) => (
                                    <option key={valor} value={valor}>{meta.label}</option>
                                ))}
                            </select>
                            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="selector">
                                <option value="todas">{t('gestorNotificaciones.todosLosEstados')}</option>
                                <option value="individuales">{t('gestorNotificaciones.individuales')}</option>
                                <option value="masivas">{t('gestorNotificaciones.masivas')}</option>
                                <option value="no_leidas">{t('gestorNotificaciones.individualesNoLeidasOpcion')}</option>
                                <option value="leidas">{t('gestorNotificaciones.individualesLeidasOpcion')}</option>
                            </select>
                            <button
                                className="gn-boton-icono"
                                onClick={() => setOrden(o => o === 'desc' ? 'asc' : 'desc')}
                                title={orden === 'desc' ? t('gestorNotificaciones.masRecientesPrimero') : t('gestorNotificaciones.masAntiguasPrimero')}
                                aria-label={orden === 'desc' ? t('gestorNotificaciones.masRecientesPrimero') : t('gestorNotificaciones.masAntiguasPrimero')}
                            >
                                {orden === 'desc' ? <FiArrowDown /> : <FiArrowUp />}
                            </button>
                            <button className="gn-boton-peligro-outline" onClick={limpiarNotificacionesAntiguas}>
                                <FiTrash2 /> {t('gestorNotificaciones.limpiarAntiguas')}
                            </button>
                        </>
                    )}
                </div>

                <div className="gn-lista">
                    <div className="gn-lista-cabecera">
                        {notificacionesActuales.length > 0 && (
                            <label className="gn-checkbox-todos">
                                <input
                                    type="checkbox"
                                    checked={todasSeleccionadasEnPagina}
                                    onChange={alternarSeleccionPagina}
                                />
                                {t('gestorNotificaciones.seleccionarPagina')}
                            </label>
                        )}
                        <p className="info-resultados">
                            {t('gestorNotificaciones.mostrandoDeNotificaciones', { visibles: notificacionesActuales.length, total: notificacionesFiltradas.length })}
                        </p>
                    </div>

                    {notificacionesActuales.length > 0 ? (
                    <div className="gn-panel">
                        {notificacionesActuales.map((notif) => {
                            const abierta = expandidas.includes(notif.id);
                            return (
                            <div
                                key={notif.id}
                                className={`gn-row ${notif.leida ? 'leida' : 'no-leida'} ${seleccionadas.includes(notif.id) ? 'seleccionada' : ''} ${abierta ? 'expandida' : ''}`}
                            >
                                <div className="gn-row-compacta" onClick={() => alternarExpandida(notif.id)}>
                                    <input
                                        type="checkbox"
                                        className="gn-card-checkbox"
                                        checked={seleccionadas.includes(notif.id)}
                                        onChange={() => alternarSeleccion(notif.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={t('gestorNotificaciones.seleccionarNotificacion')}
                                    />

                                    <span className={`gn-card-icono tipo-${notif.tipo}`}>
                                        <TipoIcono tipo={notif.tipo} />
                                    </span>

                                    <div className="gn-row-titulo">
                                        <h4>{notif.titulo}</h4>
                                        <p className="mensaje-notificacion">{notif.mensaje}</p>
                                    </div>

                                    <div className="gn-row-etiquetas">
                                        <span className={`etiqueta-prioridad prioridad-${notif.prioridad}`}>
                                            {t(`gestorNotificaciones.prioridades.${notif.prioridad}`, { defaultValue: notif.prioridad })}
                                        </span>
                                        {notif.estadisticas?.tipo === 'individual' && (
                                            <span className={`etiqueta-estado ${notif.leida ? 'estado-leida' : 'estado-no-leida'}`}>
                                                {notif.leida ? <><FiCheckCircle /> {t('gestorNotificaciones.leida')}</> : t('gestorNotificaciones.noLeida')}
                                            </span>
                                        )}
                                        {notif.estadisticas?.tipo === 'masiva' && (
                                            <span className="etiqueta-masiva"><FiUsers /> {t('gestorNotificaciones.masiva')}</span>
                                        )}
                                    </div>

                                    <span className="gn-row-fecha">{formatearFechaRelativa(notif.createdAt)}</span>

                                    <button
                                        className="boton-eliminar"
                                        onClick={(e) => { e.stopPropagation(); eliminarNotificacion(notif.id); }}
                                        title={t('gestorNotificaciones.eliminarNotificacion')}
                                        aria-label={t('gestorNotificaciones.eliminarNotificacion')}
                                    >
                                        <FiTrash2 />
                                    </button>

                                    <span className="gn-row-chevron"><FiChevronDown /></span>
                                </div>

                                {abierta && (
                                    <div className="gn-row-detalle">
                                        <div className="info-principal">
                                            <span className={`etiqueta-tipo tipo-${notif.tipo}`}>
                                                {TIPO_META[notif.tipo]?.label || notif.tipo}
                                            </span>
                                        </div>

                                        {notif.estadisticas && (
                                            <div className="seccion-estadisticas-notif">
                                                {notif.estadisticas.tipo === 'masiva' ? (
                                                    <>
                                                        <div className="stat-item">
                                                            <span className="stat-icono"><FiUsers /></span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">{notif.estadisticas.destinatarios}</span>
                                                                <span className="stat-label">{t('gestorNotificaciones.usuariosAlcanzados')}</span>
                                                            </div>
                                                        </div>
                                                        <div className="stat-item">
                                                            <span className="stat-icono"><FiCheckCircle /></span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">{t('gestorNotificaciones.verificados')}</span>
                                                                <span className="stat-label">{t('gestorNotificaciones.estadoUsuarios')}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="stat-item">
                                                            <span className="stat-icono"><FiUser /></span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">{notif.estadisticas.destinatario}</span>
                                                                <span className="stat-label">{t('gestorNotificaciones.destinatarioUnico')}</span>
                                                            </div>
                                                        </div>
                                                        <div className="stat-item">
                                                            <span className="stat-icono">{notif.estadisticas.vista ? <FiCheckCircle /> : <FiInbox />}</span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">{notif.estadisticas.vista ? t('gestorNotificaciones.leida') : t('gestorNotificaciones.noLeida')}</span>
                                                                <span className="stat-label">{t('gestorNotificaciones.estadoLectura')}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        <div className="info-secundaria">
                                            {notif.link && (
                                                <span className="gn-row-enlace" onClick={() => irAEnlace(notif)}>
                                                    <FiLink /> {notif.link} <FiExternalLink />
                                                </span>
                                            )}
                                            <span><FiUser /> {t('gestorNotificaciones.enviadoPor')} <b>{notif.enviado_por}</b></span>
                                            <span><FiCalendar /> {formatearFecha(notif.createdAt)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                    ) : (
                        <div className="gn-vacio">
                            <FiInbox />
                            <p>{t('gestorNotificaciones.sinNotificacionesEncontradas')}</p>
                            {hayFiltrosActivos && (
                                <button className="gn-boton-cancelar" onClick={limpiarFiltros}>
                                    {t('gestorNotificaciones.limpiarFiltros')}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {totalPaginas > 1 && (
                    <div className="paginacion">
                        <button
                            onClick={() => cambiarPagina(paginaActual - 1)}
                            disabled={paginaActual === 1}
                            className="boton-paginacion"
                        >
                            <FiChevronLeft /> {t('gestorNotificaciones.anterior')}
                        </button>
                        <span className="info-pagina">
                            {t('gestorNotificaciones.paginaDe', { actual: paginaActual, total: totalPaginas })}
                        </span>
                        <button
                            onClick={() => cambiarPagina(paginaActual + 1)}
                            disabled={paginaActual === totalPaginas}
                            className="boton-paginacion"
                        >
                            {t('gestorNotificaciones.siguiente')} <FiChevronRight />
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
};

export default NotificacionesManager;
